import { cn } from '@/shared/cn';
import { vizToneColor, type VizTone } from './viz-tone';

export type MeterBarProps = {
  value: number;
  max: number;
  marker?: { value: number; label: string };
  secondaryMarker?: { value: number; label: string };
  band?: { low: number; high: number };
  tone: VizTone;
  toneColor?: string;
  label?: string;
  valueLabel?: string;
  /** Print the marker label under the bar (default). Turn off in stacked lists where
   *  every row shares the same marker — caption it once at section level instead. */
  markerCaption?: boolean;
  /** Draw the marker as a target tick outside the track's clip: it overshoots
   *  the track by 3px each side and stays visible at the end of the scale.
   *  Default off (the marker is a bar inside the track, as before). */
  markerInTrack?: boolean;
  className?: string;
};

export function MeterBar({
  value,
  max,
  marker,
  secondaryMarker,
  band,
  tone,
  toneColor,
  label,
  valueLabel,
  markerCaption = true,
  markerInTrack = false,
  className,
}: MeterBarProps) {
  const scaleMax = Math.max(max, value, marker?.value ?? 0, secondaryMarker?.value ?? 0, band?.high ?? 0, 1);
  const fillPct = Math.min(100, Math.max(0, (value / scaleMax) * 100));
  const markerPct = marker ? Math.min(100, Math.max(0, (marker.value / scaleMax) * 100)) : null;
  const secondaryMarkerPct = secondaryMarker
    ? Math.min(100, Math.max(0, (secondaryMarker.value / scaleMax) * 100))
    : null;
  const bandLeft = band ? Math.min(100, Math.max(0, (band.low / scaleMax) * 100)) : null;
  const bandWidth = band
    ? Math.min(100, Math.max(0, ((band.high - band.low) / scaleMax) * 100))
    : null;
  const fillColor = toneColor ?? vizToneColor(tone);

  const track = (
    <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--ink-tertiary)_18%,transparent)]">
      {bandLeft !== null && bandWidth !== null ? (
        <div
          className="absolute inset-y-0 rounded-full"
          style={{
            left: `${bandLeft}%`,
            width: `${bandWidth}%`,
            backgroundColor: 'color-mix(in srgb, var(--state-online) 22%, transparent)',
          }}
        />
      ) : null}
      <div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${fillPct}%`, backgroundColor: fillColor }}
      />
      {markerPct !== null && !markerInTrack ? (
        <div
          className="absolute inset-y-0 w-0.5 bg-[var(--ink-primary)]"
          style={{ left: `${markerPct}%` }}
          title={marker?.label}
          aria-label={marker?.label}
        />
      ) : null}
      {secondaryMarkerPct !== null ? (
        <div
          className="absolute inset-y-0 w-0.5 border-l border-dashed border-[var(--ink-tertiary)]"
          style={{ left: `${secondaryMarkerPct}%` }}
          title={secondaryMarker?.label}
          aria-label={secondaryMarker?.label}
        />
      ) : null}
    </div>
  );

  return (
    <div className={cn('space-y-1.5', className)}>
      {(label || valueLabel) && (
        <div className="flex items-baseline justify-between gap-2 text-xs">
          {label ? (
            <span className="font-medium text-ink-primary">{label}</span>
          ) : (
            <span />
          )}
          {valueLabel ? (
            <span className="tabular-nums text-muted">{valueLabel}</span>
          ) : null}
        </div>
      )}
      {markerInTrack && markerPct !== null ? (
        <div className="relative">
          {track}
          <div
            className="absolute -inset-y-[3px] w-0.5 rounded-[1px] bg-[var(--ink-primary)] shadow-[0_0_0_1px_var(--surface-2)]"
            style={{ left: `min(${markerPct}%, calc(100% - 2px))` }}
            title={marker?.label}
            aria-label={marker?.label}
          />
        </div>
      ) : (
        track
      )}
      {markerCaption ? (
        <div className="space-y-0.5">
          {marker?.label ? <p className="text-[10px] text-muted">{marker.label}</p> : null}
          {secondaryMarker?.label ? (
            <p className="text-[10px] text-muted">{secondaryMarker.label}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
